import React, { useMemo, useState } from 'react';
import ChartModal from './ChartModal';
import BeneishBreakdown from './BeneishBreakdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatForensicValue, heatmapZone } from '../utils/forensicDisplay';

const vsArrow = (dir) => {
    if (dir === 'up') return '↑';
    if (dir === 'down') return '↓';
    if (dir === 'flat') return '→';
    return '';
};

export default function ForensicHeatmap({
    rows = [],
    financials,
    companyName,
    defaultOpen = false,
}) {
    const [open, setOpen] = useState(defaultOpen);
    const [showChartModal, setShowChartModal] = useState(false);
    const [chartRatio, setChartRatio] = useState(null);
    const [beneishBreakdown, setBeneishBreakdown] = useState(null);

    const years = useMemo(() => {
        const set = new Set();
        rows.forEach((row) => {
            (row.values || []).forEach((point) => {
                if (point?.year) set.add(String(point.year));
            });
        });
        return Array.from(set).sort((a, b) => Number(a) - Number(b));
    }, [rows]);

    if (!rows.length) {
        return <p className="forensic-empty">No historical forensic ratios available.</p>;
    }

    const byYear = (row) => {
        const map = {};
        (row.values || []).forEach((point) => {
            map[String(point.year)] = point;
        });
        return map;
    };

    return (
        <div className="forensic-heatmap">
            <button
                type="button"
                className={`forensic-heatmap__toggle ${open ? 'is-open' : ''}`}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                <span>Full history ({years[0] || '—'}–{years[years.length - 1] || '—'})</span>
                <span className="forensic-heatmap__hint">opt-in drill-down</span>
            </button>
            {open && (
                <div className="table-wrapper forensic-heatmap__table">
                    <table>
                        <thead>
                            <tr>
                                <th>Ratio</th>
                                {years.map((year) => (
                                    <th key={year}>{year}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const lookup = byYear(row);
                                return (
                                    <tr key={row.name}>
                                        <td>
                                            <div className="ratio-name-cell">
                                                <span>{row.displayName}</span>
                                                <div className="ratio-actions">
                                                    <FontAwesomeIcon
                                                        icon="chart-line"
                                                        title="View Chart"
                                                        onClick={() => {
                                                            setChartRatio(row);
                                                            setShowChartModal(true);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        {years.map((year) => {
                                            const point = lookup[year];
                                            const value = point?.value;
                                            const zone = heatmapZone(row.name, value);
                                            const isBeneish = row.name === 'beneishMScore';
                                            const hasBreakdown = isBeneish && Array.isArray(point?.components) && point.components.length > 0;
                                            const arrow = row.colorMode === 'neutral' ? vsArrow(point?.vsMedian) : '';
                                            const heatClass = zone ? `forensic-heat forensic-heat--${zone}` : '';
                                            return (
                                                <td
                                                    key={year}
                                                    className={heatClass}
                                                    data-zone={zone || undefined}
                                                    data-year={year}
                                                    data-metric={row.name}
                                                    style={{
                                                        cursor: hasBreakdown ? 'pointer' : 'default',
                                                    }}
                                                    title={hasBreakdown ? `Click to see what drove this score in FY${year}` : undefined}
                                                    onClick={hasBreakdown ? () => setBeneishBreakdown(point) : undefined}
                                                >
                                                    {formatForensicValue(row.name, value, row.unit)}
                                                    {arrow ? ` ${arrow}` : ''}
                                                    {hasBreakdown && (
                                                        <span className="beneish-drill-icon" aria-hidden="true"> ⓘ</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <ChartModal
                isOpen={showChartModal}
                ratio={chartRatio}
                allRatios={rows}
                financials={financials}
                companyName={companyName}
                onClose={() => setShowChartModal(false)}
            />
            {beneishBreakdown && (
                <BeneishBreakdown
                    year={beneishBreakdown.year}
                    mScore={beneishBreakdown.value}
                    zone={beneishBreakdown.color}
                    components={beneishBreakdown.components}
                    topDrivers={beneishBreakdown.topDrivers}
                    scoreExplain={beneishBreakdown.scoreExplain}
                    onClose={() => setBeneishBreakdown(null)}
                />
            )}
        </div>
    );
}
